import type { CustomizationBatchResponse, CustomizationContextResult } from "api/realesyStudioApi";

const MONGO_ID_RE = /^[a-fA-F0-9]{24}$/;

export function isMongoObjectId(id: string | null | undefined): boolean {
  const s = String(id ?? "").trim();
  return MONGO_ID_RE.test(s) && !/^0{24}$/.test(s);
}

/** Aligns with SearchBrokerageAgentTeam.getEffectiveLicenseType (subset for restore matching). */
export function effectiveLicenseTypeForItem(item: any): string {
  if (!item) return "AGENT";
  const raw = String(item.license_type ?? "").trim();
  const u = raw.toUpperCase();
  if (u === "OFFICE") return "OFFICE";
  const val = String(item.value ?? item.id ?? item._id ?? "").trim();
  const off = item.offices;
  if (off && typeof off === "object") {
    const oid = String(off._id ?? off.id ?? "").trim();
    if (oid && val && oid === val) return "OFFICE";
  }
  if (u === "BROKERAGE_AGENT") return "AGENT";
  const order = ["BROKERAGE_FIRM", "OFFICE", "AGENT", "BROKERAGE_TEAM_MEMBER", "TEAM_MEMBER"];
  if (order.includes(u)) return u;
  return u || "AGENT";
}

/**
 * API may return `contexts` or `items[]` with nested `customization` / `active_context_id`.
 */
export function normalizeCustomizationBatchContexts(batch: CustomizationBatchResponse): CustomizationContextResult[] {
  const b = batch as Record<string, unknown>;
  const ctx = b.contexts;
  if (Array.isArray(ctx) && ctx.length > 0) {
    return ctx as CustomizationContextResult[];
  }
  const items = b.items;
  if (!Array.isArray(items)) return [];

  return items.map((it: any) => {
    const c = (it?.customization && typeof it.customization === "object" ? it.customization : {}) as Record<
      string,
      unknown
    >;
    const contextId = String(c.context_id ?? it?.active_context_id ?? it?.context_id ?? "");
    return {
      context_id: contextId,
      design_id: (c.design_id as string) ?? (c.designId as string) ?? it?.design_id ?? it?.designId,
      preview_id: (c.preview_id as string) ?? (c.previewId as string) ?? it?.preview_id ?? it?.previewId,
      design_name: (c.design_name as string) ?? (c.designName as string) ?? it?.design_name ?? it?.designName,
      product_id: (it?.product_id as string) ?? (c.product_id as string),
      variant_id: (it?.variant_id as string) ?? (c.variant_id as string),
      line_item_key: (it?.line_item_key as string) ?? (c.line_item_key as string) ?? (it?.variant_id as string),
      ...c,
    } as CustomizationContextResult;
  });
}

/**
 * Search strings for Studio restore — same priority as a human would search:
 * brokerage → team member → agent → customer full name (deduped).
 * Mirrors the normal flow: open search, try the most specific name first.
 */
export function buildStudioRestoreSearchQueries(order: Record<string, any>): string[] {
  const out: string[] = [];
  const push = (s: string) => {
    const t = s.trim();
    if (t && !out.includes(t)) out.push(t);
  };
  push(String(order.brokerage_firm_name ?? order.brokerage_name ?? ""));
  push(String(order.team_member_name ?? ""));
  push(String(order.agent_name ?? ""));
  const fullName = [order.customer_first_name, order.customer_last_name].filter(Boolean).join(" ").trim();
  push(fullName);
  return out;
}

/**
 * Runs the same `created-or-linked-detailed` search the buyer picker uses, trying each
 * query from {@link buildStudioRestoreSearchQueries} until {@link findSearchMatchFromResults} finds a row.
 */
export async function findBuyerSearchMatchForStudioRestore(
  apiBase: string,
  token: string,
  customerGroupId: string,
  batchOrder: Record<string, any>,
  isCancelled: () => boolean
): Promise<any | null> {
  const queries = buildStudioRestoreSearchQueries(batchOrder);
  const qList = queries.length ? queries : [""];
  for (const q of qList) {
    if (isCancelled()) return null;
    const list = await fetchCreatedOrLinkedDetailed(apiBase, token, customerGroupId, q);
    if (isCancelled()) return null;
    const match = findSearchMatchFromResults(batchOrder, list);
    if (match) return match;
  }
  return null;
}

/** Map batch billing/shipping snapshot + loaded address list → form + selected address objects. */
export function studioRestoreAddressBindings(
  batchOrder: Record<string, any>,
  addrList: any[]
): {
  notes: string;
  formik: {
    isSameAsBilling: boolean;
    billingAddressId: string;
    shippingAddressId: string;
    notes: string;
  };
  useOfficeBilling: boolean;
  billingAddressObj: any | null;
  shippingAddressObj: any | null;
} {
  const hasOfficeOnOrder = !!(batchOrder.office?._id || batchOrder.office?.id);
  const useOfficeBilling = !batchOrder.agent_id && hasOfficeOnOrder;

  const bilRaw = batchOrder.billing_address;
  const shipRaw = batchOrder.shipping_address;
  const bilId = typeof bilRaw === "string" ? bilRaw : bilRaw?._id;
  const shipId = typeof shipRaw === "string" ? shipRaw : shipRaw?._id;
  const same = !!batchOrder.both_address_same;

  const listAddrs = Array.isArray(addrList) && addrList.length ? addrList : [];
  const findAddr = (id: string | undefined) =>
    id ? listAddrs.find((a: any) => String(a._id) === String(id)) : null;

  const bilAddr = bilId ? findAddr(String(bilId)) : null;
  const shipAddr = shipId && !same ? findAddr(String(shipId)) : null;
  const notes = String(batchOrder.notes ?? "").trim();

  return {
    notes,
    formik: {
      isSameAsBilling: same,
      billingAddressId: bilId ? String(bilId) : "",
      shippingAddressId: same ? String(bilId || "") : String(shipId || ""),
      notes,
    },
    useOfficeBilling,
    billingAddressObj: bilAddr,
    shippingAddressObj: same ? bilAddr || null : shipAddr || null,
  };
}

export type StudioRestoreChargesSeeds = {
  shippingInitialIds: ReturnType<typeof shippingInitialSelectedIdsFromOrder> | null;
  shippingSavedRows: any[] | null;
  specialFeeInitialIds: string[] | null;
};

/** Shipping + special-fee UI seeds from batch `order_charges` (after buyer + address chain has run). */
export function studioRestoreChargesSeedsFromOrder(batchOrder: Record<string, any>): StudioRestoreChargesSeeds {
  const shipRows = shippingUiRowsFromOrderSnapshot(batchOrder);
  const shipInitIds = shippingInitialSelectedIdsFromOrder(batchOrder);
  const hasShipSeed = !!(shipInitIds && shipRows.length > 0);
  const feeIds = specialFeeIdsFromOrderSnapshot(batchOrder);
  return {
    shippingInitialIds: hasShipSeed ? shipInitIds : null,
    shippingSavedRows: hasShipSeed ? shipRows : null,
    specialFeeInitialIds: feeIds.length ? feeIds : null,
  };
}

function searchRowId(r: any): string {
  return String(r?.value ?? r?.id ?? r?._id ?? "").trim();
}

/**
 * Pick the search row that matches the batch order (prefer brokerage firm row when brokerage_firm_id is set).
 */
export function findSearchMatchFromResults(order: Record<string, any>, list: any[]): any | null {
  if (!Array.isArray(list) || !list.length) return null;

  const tryId = (raw: unknown) => {
    const sid = String(raw ?? "").trim();
    if (!isMongoObjectId(sid)) return null;
    return list.find((r) => searchRowId(r) === sid) ?? null;
  };

  const bf = String(order.brokerage_firm_id ?? "").trim();
  if (isMongoObjectId(bf)) {
    const sameId = list.filter((r) => searchRowId(r) === bf);
    const firm = sameId.find((r) => effectiveLicenseTypeForItem(r) === "BROKERAGE_FIRM");
    if (firm) return firm;
    if (sameId.length === 1) return sameId[0];
  }

  return (
    tryId(order.team_member_id) ||
    tryId(order.agent_id) ||
    tryId(order.office?._id) ||
    tryId(order.office?.id) ||
    tryId(order.brokerage_firm_id) ||
    null
  );
}

export async function fetchCreatedOrLinkedDetailed(
  apiBase: string,
  token: string,
  customerGroupId: string,
  query: string,
  page = 1,
  limit = 50
): Promise<any[]> {
  const params = new URLSearchParams({
    query: String(query ?? "").trim(),
    page: String(page),
    limit: String(limit),
  });
  const url = `${apiBase.replace(/\/+$/, "")}/users/${customerGroupId}/created-or-linked-detailed?${params}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  if (!res.ok) return [];
  const json = await res.json().catch(() => ({}));
  return Array.isArray(json?.data) ? json.data : [];
}

export function contactFromOrderSnapshot(order: Record<string, any>) {
  return {
    fName: String(order.customer_first_name ?? "").trim(),
    lName: String(order.customer_last_name ?? "").trim(),
    email: String(order.customer_email ?? "").trim(),
    phone: String(order.customer_phone ?? "").trim() || "0000000000",
  };
}

/**
 * Maps batch `shipping_id` to ShippingOptions `initialSelectedIds` after `/shipping-costs` / UPS lists load.
 * Mongo-style ids (and `custom-manual`) → manual; anything else → UPS service code.
 */
export function shippingInitialSelectedIdsFromOrder(order: Record<string, any>): {
  manual_shipping_id: string | null;
  ups_service_code: string | null;
  integration_id: string | null;
  shipper_address_id: string | null;
} | null {
  const rows = shippingUiRowsFromOrderSnapshot(order);
  const sid = String(rows[0]?.shipping__id ?? "").trim();
  if (!sid) return null;
  const blank = { integration_id: null as string | null, shipper_address_id: null as string | null };
  if (isMongoObjectId(sid) || sid === "custom-manual") {
    return { ...blank, manual_shipping_id: sid, ups_service_code: null };
  }
  return { ...blank, manual_shipping_id: null, ups_service_code: sid };
}

export function shippingUiRowsFromOrderSnapshot(order: Record<string, any>): any[] {
  const charges = order?.order_charges;
  const first = Array.isArray(charges) ? charges[0] : null;
  const opts = first?.shipping_option;
  if (!Array.isArray(opts)) return [];
  return opts.map((r: any) => {
    const sid = String(r.shipping_id ?? r.shipping__id ?? "");
    return {
      shipping__id: sid,
      shipping_id: sid,
      shipping_name: String(r.shipping_name ?? ""),
      shipping_amount: Number(r.shipping_amount || 0),
      shipping_tax_percentage: Number(r.shipping_tax_percentage || 0),
      shipping_tax_amount: Number(r.shipping_tax_amount || 0),
      shipping_taxable:
        typeof r.shipping_taxable === "boolean"
          ? r.shipping_taxable
          : true,
      shipping_delivarydate: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
      shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
    };
  });
}

export function specialFeeIdsFromOrderSnapshot(order: Record<string, any>): string[] {
  const charges = order?.order_charges;
  const first = Array.isArray(charges) ? charges[0] : null;
  const fees = first?.specialfee;
  if (!Array.isArray(fees)) return [];
  return fees
    .map((f: any) => String(f.specialfee_id ?? f.specialfeeId ?? f._id ?? "").trim())
    .filter(Boolean);
}

/** Stable key for matching catalog rows to `getCustomizationBatch` items. */
export function studioBatchVariantKey(productId: string, variantId: string): string {
  return `${String(productId ?? "").trim()}|${String(variantId ?? "").trim()}`;
}

export type StudioBatchVariantPreviewEntry = {
  previewFileId: string;
  designId: string;
  designName: string;
};

/**
 * From batch `items[]`: when an item has `customization.design_id` + `preview_id` (work-drive file),
 * map `product_id|variant_id` → Studio metadata for picker + order line items.
 */
export function studioBatchVariantPreviewsFromBatchItems(
  batch: { items?: unknown[] } | null | undefined
): Record<string, StudioBatchVariantPreviewEntry> {
  const out: Record<string, StudioBatchVariantPreviewEntry> = {};
  const items = batch && Array.isArray(batch.items) ? batch.items : [];
  for (const raw of items) {
    const it = raw as Record<string, unknown>;
    const pid = String(it?.product_id ?? "").trim();
    const vid = String(it?.variant_id ?? "").trim();
    const cust =
      it?.customization && typeof it.customization === "object"
        ? (it.customization as Record<string, unknown>)
        : {};
    const designId = String(cust.design_id ?? cust.designId ?? "").trim();
    const previewId = String(cust.preview_id ?? cust.previewId ?? "").trim();
    const designName = String(cust.design_name ?? cust.designName ?? "").trim();
    if (!pid || !vid) continue;
    if (!designId || !isMongoObjectId(designId)) continue;
    if (!previewId || !isMongoObjectId(previewId)) continue;
    out[studioBatchVariantKey(pid, vid)] = {
      previewFileId: previewId,
      designId,
      designName,
    };
  }
  return out;
}

/**
 * Build the same `product_id|variant_id` → preview map from cart / order lines that already
 * carry `design_id` + `preview_url` (work-drive file id). Accepts `productId` / `variantId` too.
 */
export function studioBatchVariantPreviewsFromCartLines(
  lines: readonly any[] | null | undefined
): Record<string, StudioBatchVariantPreviewEntry> {
  const out: Record<string, StudioBatchVariantPreviewEntry> = {};
  if (!Array.isArray(lines) || lines.length === 0) return out;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const pid = String(row["product_id"] ?? row["productId"] ?? "").trim();
    const vid = String(row["variant_id"] ?? row["variantId"] ?? "").trim();
    const designId = String(row["design_id"] ?? row["designId"] ?? "").trim();
    const previewFileId = String(row["preview_url"] ?? row["previewUrl"] ?? "").trim();
    const designName = String(row["design_name"] ?? row["designName"] ?? "").trim();
    if (!pid || !vid) continue;
    if (!designId || !isMongoObjectId(designId)) continue;
    if (!previewFileId || !isMongoObjectId(previewFileId)) continue;
    out[studioBatchVariantKey(pid, vid)] = {
      previewFileId,
      designId,
      designName,
    };
  }
  return out;
}
